import TagSelector from "@/components/applications/shared/tagSelector";
import { useQuery } from "@apollo/client/react";
import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Divider } from "@repo/components/src/ui/divider";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import PaymentBreakdown from "@repo/components/src/shared/paymentBreakdown";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import { formatMoneyFromFragment } from "@repo/components/src/utils/money";
import { PostulationDetailDocument } from "@repo/graphql/documents/postulation/query.generated";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { GetUserProfileQuery } from "@repo/graphql/documents/user/query.generated";

type Props = {
  brandId: string;
  campaignId: string;
  user: GetUserProfileQuery["userResource"]["get"];
};

const SharedApplication = React.memo((props: Props) => {
  const user = props.user;
  const { data, loading } = useQuery(PostulationDetailDocument, {
    variables: {
      campaignId: props.campaignId,
      userId: user.id,
    },
  });
  const postulation = data?.postulationResource.getForUser;
  const tags = useMemo(
    () => data?.postulationResource.getForUser?.tags ?? [],
    [data],
  );

  if (!loading && !data?.postulationResource.getForUser)
    return <Text action="muted">Este usuario no postuló a esta campaña.</Text>;
  return (
    <HStack flexSpace="md">
      {data?.postulationResource.getForUser && (
        <Box className="w-full">
          <Card variant="outline" size="lg">
            <Heading bold className="mb-4">
              Tags
            </Heading>
            <TagSelector
              brandId={props.brandId}
              postulationId={data.postulationResource.getForUser.id}
              tags={tags}
            />
          </Card>
        </Box>
      )}
      <Box className="w-full">
        <Skeleton
          variant="rounded"
          isLoaded={!loading}
          className="aspect-video"
        >
          <Card variant="outline" size="lg" className="h-full">
            <Heading bold className="mb-4">
              Cotización
            </Heading>
            <Text size="lg">
              {formatMoneyFromFragment(postulation?.amountOffered)} líquido.
            </Text>
            <Divider className="my-4" />
            {postulation && (
              <PaymentBreakdown
                lines={postulation.paymentBreakdown.lines}
                density="full"
              />
            )}
          </Card>
        </Skeleton>
      </Box>
      <Box className="w-full">
        <Skeleton
          variant="rounded"
          isLoaded={!loading}
          className="aspect-video"
        >
          <Card variant="outline" size="lg" className="h-full">
            <Heading bold className="mb-4">
              Detalles de postulación
            </Heading>
            <Box>
              <Text>Fecha de postulación:</Text>
              <Text size="sm" action="muted" className="capitalize">
                {dayjs(postulation?.createdAt).localizedShortDate()}
              </Text>
            </Box>
            {data?.postulationResource.getForUser?.postulationanswerSet.map(
              (answerFragment, index) => {
                const question = answerFragment.question;
                const answer = answerFragment;
                return (
                  <React.Fragment key={index}>
                    <Divider className="my-4" />
                    <Box key={answer.id}>
                      <Text>"{question.question}"</Text>
                      <Text size="sm" action="muted">
                        "{answer.answer}"
                      </Text>
                    </Box>
                  </React.Fragment>
                );
              },
            )}
            {!data?.postulationResource.getForUser?.postulationanswerSet
              .length && <Text action="muted">No hay respuestas</Text>}
          </Card>
        </Skeleton>
      </Box>
      <Box className="w-full">
        <Skeleton
          variant="rounded"
          isLoaded={!loading}
          className="aspect-video"
        >
          <Card variant="outline" size="lg" className="h-full">
            <Heading bold className="mb-4">
              Comentarios de {user.firstName}
            </Heading>
            <Text action="muted">{postulation?.comment || "-"}</Text>
          </Card>
        </Skeleton>
      </Box>
    </HStack>
  );
});

SharedApplication.displayName = "SharedApplication";

export default SharedApplication;
